import { useMemo, useState } from 'react'
import { AdminTicket, TicketPriority, TicketStatus } from '../lib/domain'
import { useRemoteTickets } from '../lib/useRemoteData'
import {
  patchTicket as patchTicketRemote,
  replyTicket as replyTicketRemote,
  getTicket as getTicketRemote,
  type RemoteTicketReply
} from '../lib/adminClient'
import { useEffect } from 'react'
import { LoadError } from '../components/LoadError'
import { AccountLink, DrillValue } from '../components/Drill'

const PRIORITY_ORDER: Record<TicketPriority, number> = { urgent: 0, high: 1, normal: 2, low: 3 }
const STATUS_LABEL: Record<TicketStatus, string> = { open: 'Open', pending: 'Pending', resolved: 'Resolved', closed: 'Closed' }

function priorityTone(p: TicketPriority) {
  if (p === 'urgent') return 'bg-red-500/15 text-red-300'
  if (p === 'high') return 'bg-orange-500/15 text-orange-300'
  if (p === 'normal') return 'bg-accent/15 text-accent'
  return 'bg-slate-500/15 text-slate-300'
}

function statusTone(s: TicketStatus) {
  if (s === 'open') return 'bg-amber-500/15 text-amber-300'
  if (s === 'pending') return 'bg-indigo-500/15 text-indigo-300'
  if (s === 'resolved') return 'bg-emerald-500/15 text-emerald-300'
  return 'bg-slate-500/15 text-slate-300'
}

export default function Tickets() {
  const [filter, setFilter] = useState<'all' | TicketStatus>('all')
  const [selected, setSelected] = useState<AdminTicket | null>(null)
  const [reply, setReply] = useState('')
  const [overrides, setOverrides] = useState<Record<string, Partial<AdminTicket>>>({})
  const [draftReplies, setDraftReplies] = useState<Record<string, string[]>>({})
  const { data: remoteTickets, refresh: refreshTickets, error: loadError, loading } = useRemoteTickets()
  // A failed write is shown and rolled back, never left looking saved.
  const [actionError, setActionError] = useState<string | null>(null)
  const [persistedReplies, setPersistedReplies] = useState<RemoteTicketReply[]>([])

  // When a ticket is selected, fetch its persisted reply thread from
  // the server. Keeps `persistedReplies` separate from the local
  // optimistic draftReplies so we can show both (server replies on
  // top, then any drafts not yet round-tripped).
  useEffect(() => {
    if (!selected) {
      setPersistedReplies([])
      return
    }
    let cancelled = false
    void getTicketRemote(selected.id).then((r) => {
      if (cancelled) return
      if (r.ok) setPersistedReplies(r.data.replies ?? [])
    })
    return () => { cancelled = true }
  }, [selected?.id])

  const merged: AdminTicket[] = useMemo(
    () => remoteTickets.map((t) => ({ ...t, ...overrides[t.id] })),
    [overrides, remoteTickets]
  )

  const visible = useMemo(() => {
    const list = filter === 'all' ? merged : merged.filter((t) => t.status === filter)
    return [...list].sort((a, b) => {
      if (PRIORITY_ORDER[a.priority] !== PRIORITY_ORDER[b.priority]) {
        return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
      }
      return a.updatedAt < b.updatedAt ? 1 : -1
    })
  }, [merged, filter])

  async function setStatus(id: string, status: TicketStatus): Promise<void> {
    // Optimistic — flip the UI immediately, persist in the background.
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], status, updatedAt: new Date().toISOString() } }))
    setSelected((s) => (s && s.id === id ? { ...s, status } : s))
    setActionError(null)
    const result = await patchTicketRemote(id, { status })
    if (!result.ok) {
      // Roll back the optimistic change so the list shows the stored status.
      setOverrides((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
      const stored = remoteTickets.find((t) => t.id === id)
      setSelected((s) => (s && s.id === id && stored ? { ...s, status: stored.status } : s))
      setActionError(`Could not change the status: ${result.error}`)
      return
    }
    await refreshTickets()
    setOverrides((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  async function sendReply(): Promise<void> {
    if (!selected || !reply.trim()) return
    const body = reply.trim()
    setReply('')
    // Optimistic — show the reply locally before the round-trip lands.
    setDraftReplies((prev) => ({
      ...prev,
      [selected.id]: [...(prev[selected.id] ?? []), body]
    }))
    setOverrides((prev) => ({
      ...prev,
      [selected.id]: {
        ...prev[selected.id],
        replyCount: (prev[selected.id]?.replyCount ?? selected.replyCount) + 1,
        status: 'pending',
        updatedAt: new Date().toISOString()
      }
    }))
    setActionError(null)
    const result = await replyTicketRemote(selected.id, body)
    if (!result.ok) {
      // The reply was not stored: drop the "sending" draft and the optimistic
      // count/status, and give the text back so nothing typed is lost.
      setDraftReplies((prev) => {
        const next = { ...prev }
        delete next[selected.id]
        return next
      })
      setOverrides((prev) => {
        const next = { ...prev }
        delete next[selected.id]
        return next
      })
      setReply(body)
      setActionError(`Reply not sent: ${result.error}`)
      return
    }
    // Append the persisted reply so the user sees it land in-place.
    setPersistedReplies((prev) => [...prev, result.data.reply])
    // Server now owns the reply + the auto-bumped status. Refresh the
    // list so updatedAt + counts come from the source of truth, and
    // clear the local optimistic override + the draft.
    await refreshTickets()
    setOverrides((prev) => {
      const next = { ...prev }
      delete next[selected.id]
      return next
    })
    setDraftReplies((prev) => {
      const next = { ...prev }
      delete next[selected!.id]
      return next
    })
  }

  const counts = useMemo(() => {
    const c: Record<TicketStatus | 'all', number> = { all: merged.length, open: 0, pending: 0, resolved: 0, closed: 0 }
    merged.forEach((t) => { c[t.status]++ })
    return c
  }, [merged])

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Support</div>
          <h1 className="text-2xl font-semibold mt-1">Tickets</h1>
        </div>
        <div className="flex gap-2">
          {(['all', 'open', 'pending', 'resolved', 'closed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider border ${
                filter === f ? 'border-accent bg-accent/10 text-white' : 'border-white/10 text-slate-300 hover:bg-white/5'
              }`}
            >
              {f} <span className="ml-1 text-slate-500">{counts[f]}</span>
            </button>
          ))}
        </div>
      </div>

      {loadError && <LoadError what="tickets" endpoint="GET /admin/tickets" error={loadError} />}
      {actionError && (
        <div role="alert" className="rounded-xl p-3 mb-4 text-sm bg-red-500/10 border border-red-500/30 text-red-200">{actionError}</div>
      )}
      {!loadError && !loading && remoteTickets.length === 0 && (
        <div className="glass-card rounded-xl p-6 mb-4 text-sm text-slate-400">No support tickets yet.</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4">
        <div className="glass-card rounded-xl overflow-hidden">
          <ul className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
            {visible.map((t) => (
              <li
                key={t.id}
                onClick={() => setSelected(t)}
                className={`px-4 py-3 cursor-pointer transition-colors ${selected?.id === t.id ? 'bg-accent/10' : 'hover:bg-white/5'}`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${priorityTone(t.priority)}`}>{t.priority}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${statusTone(t.status)}`}>{STATUS_LABEL[t.status]}</span>
                </div>
                <div className="text-sm font-medium truncate">{t.subject}</div>
                <div className="text-xs text-slate-400 mt-1 truncate">{t.userEmail} · {t.category} · {t.replyCount} replies</div>
              </li>
            ))}
            {visible.length === 0 && (
              <li className="py-12 text-center text-slate-500">No tickets match this filter.</li>
            )}
          </ul>
        </div>

        <div className="glass-card rounded-xl p-5 min-h-[300px]">
          {!selected && (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm">Select a ticket to view the conversation.</div>
          )}
          {selected && (
            <div className="flex flex-col h-full">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-lg font-semibold">{selected.subject}</h2>
                  <div className="text-xs text-slate-400 mt-1">
                    <AccountLink id={selected.userId}>{selected.userEmail}</AccountLink> · {selected.category} ·{' '}
                    <DrillValue to={{ set: 'tickets', filters: { id: selected.id } }} label={`Ticket “${selected.subject}”`} testId="ticket-record">
                      the record
                    </DrillValue>
                  </div>
                </div>
                <select
                  value={selected.status}
                  onChange={(e) => void setStatus(selected.id, e.target.value as TicketStatus)}
                  className="bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-accent"
                >
                  {(['open', 'pending', 'resolved', 'closed'] as TicketStatus[]).map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-3 flex-1 overflow-y-auto max-h-[50vh] pr-1">
                <div className="border border-white/5 rounded-lg p-3 bg-black/20">
                  <div className="text-[11px] text-slate-400 mb-1">{selected.userEmail} · {selected.createdAt.slice(0, 10)}</div>
                  <div className="text-sm text-slate-200 whitespace-pre-line">{selected.body}</div>
                </div>
                {persistedReplies.map((r) => (
                  <div key={r.id} className={`border rounded-lg p-3 ${r.authorKind === 'admin' ? 'border-accent/20 bg-accent/5 ml-6' : 'border-white/5 bg-black/20'}`}>
                    <div className={`text-[11px] mb-1 ${r.authorKind === 'admin' ? 'text-accent' : 'text-slate-400'}`}>
                      {r.authorEmail} · {new Date(r.createdAt).toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-200 whitespace-pre-line">{r.body}</div>
                  </div>
                ))}
                {/* Local drafts not yet round-tripped (rare — these flip
                    to persisted on the next refresh after a send). */}
                {(draftReplies[selected.id] ?? []).map((r, i) => (
                  <div key={`draft-${i}`} className="border border-accent/20 rounded-lg p-3 bg-accent/5 ml-6 opacity-70">
                    <div className="text-[11px] text-accent mb-1">support · sending…</div>
                    <div className="text-sm text-slate-200 whitespace-pre-line">{r}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-3 border-t border-white/5">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={3}
                  placeholder="Type a reply…"
                  className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent resize-none"
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button className="btn-ghost" onClick={() => setReply('')}>Discard</button>
                  <button className="btn-primary" onClick={() => void sendReply()} disabled={!reply.trim()}>Send reply</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
